"use client";

/**
 * Export wizard — clones the Request Integration modal layout from the
 * integrations settings page. Two-panel design with branded left sidebar,
 * numbered step indicator, and animated step transitions.
 *
 * All wizard state lives in ExportWizardProvider (./context.tsx); the shell
 * and step components read it via useExportWizard().
 */

import { AnimatePresence } from "framer-motion";

import { Modal, ModalContent, ModalTitle } from "@/components/ui/modal";
import { StepPanel, WizardStepIndicator } from "@/components/wizard";

import { ExportWizardProvider, useExportWizard } from "./context";
import { ExportWizardCloseButton, ExportWizardSidebar } from "./layout";
import { StepFormat } from "./step-format";
import { StepOptions } from "./step-options";
import { StepReview } from "./step-review";
import type { ExportFormat, ExportOptions, ExportSource } from "./types";

export type { ExportOptions } from "./types";

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  profileCount: number;
  /** Export selection — when provided, the modal asks the backend to
   * pre-calculate profile/revealed-contact counts and credit costs. */
  source?: ExportSource | null;
  onExport: (
    format: ExportFormat,
    includeContacts: boolean,
    options: ExportOptions,
  ) => void;
}

const STEP_LABELS = ["Format", "Options", "Review"];

const SIDEBAR_FEATURES = [
  "Export as PDF, CSV, or both",
  "Include contact information",
  "Per-profile credit billing",
  "Privacy options from your org policy",
];

export function ExportModal({
  open,
  onClose,
  profileCount,
  source,
  onExport,
}: ExportModalProps) {
  if (profileCount === 0) return null;

  return (
    <ExportWizardProvider
      open={open}
      profileCount={profileCount}
      source={source}
      onClose={onClose}
      onExport={onExport}
    >
      <ExportModalShell open={open} onClose={onClose} />
    </ExportWizardProvider>
  );
}

function ExportModalShell({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { step } = useExportWizard();

  return (
    <Modal open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <ModalContent size="lg" className="max-h-[92dvh] p-0 sm:max-w-180">
        <div className="sr-only">
          <ModalTitle>Export Profiles</ModalTitle>
        </div>

        <div className="relative flex min-h-0 flex-col overflow-hidden sm:h-143.5 sm:flex-row">
          <ExportWizardSidebar
            title={
              <>
                Export your
                <br />
                candidate profiles.
              </>
            }
            description="Configure your export format, choose options, and review the credit cost before exporting."
            features={SIDEBAR_FEATURES}
          />

          {/* ── Right content panel ── */}
          <div className="relative flex min-h-0 flex-1 flex-col bg-surface-elevated">
            <ExportWizardCloseButton onClose={onClose} />

            <WizardStepIndicator labels={STEP_LABELS} current={step} />

            {/* Step content */}
            <AnimatePresence mode="wait">
              {step === 1 && (
                <StepPanel key="s1">
                  <StepFormat />
                </StepPanel>
              )}
              {step === 2 && (
                <StepPanel key="s2">
                  <StepOptions />
                </StepPanel>
              )}
              {step === 3 && (
                <StepPanel key="s3">
                  <StepReview />
                </StepPanel>
              )}
            </AnimatePresence>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
}
